import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Briefcase, MapPin, Clock, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobOffersPage() {
  const { offerId } = useParams();
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (offerId) {
      fetchSingleOffer();
    } else {
      fetchOffers();
    }
  }, [offerId]);

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/job-offers?active_only=true`);
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching job offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleOffer = async () => {
    try {
      const response = await axios.get(`${API}/job-offers/${offerId}`);
      setSelectedOffer(response.data);
    } catch (error) {
      console.error('Error fetching job offer:', error);
    } finally {
      setLoading(false);
    }
  };

  // Single offer detail view
  if (offerId && selectedOffer) {
    return (
      <div data-testid="job-offer-detail">
        <Navbar />
        <main>
          <section className="hero-dark py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Link to="/offerte-lavoro" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6">
                <ArrowLeft size={20} />
                Torna alle offerte
              </Link>
              <div className="accent-line mb-6"></div>
              <div className="flex items-start gap-4 mb-4">
                <span className="bg-[#27AE60] text-white px-4 py-2 font-bold">{selectedOffer.contract_type}</span>
                <span className="bg-white/10 text-white px-4 py-2">{selectedOffer.experience_required}</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
                {selectedOffer.title}
              </h1>
              <div className="flex flex-wrap gap-6 text-gray-300">
                <span className="flex items-center gap-2"><MapPin size={18} /> {selectedOffer.location}</span>
                {selectedOffer.company && <span className="flex items-center gap-2"><Briefcase size={18} /> {selectedOffer.company}</span>}
                {selectedOffer.salary_range && <span className="text-[#27AE60] font-bold">{selectedOffer.salary_range}</span>}
              </div>
            </div>
          </section>

          <section className="py-16 bg-[#F5F5F7]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-8">
                  <div className="bg-white p-8 border-2 border-gray-200">
                    <h2 className="text-xl font-bold text-[#1A1A2E] mb-4">Descrizione</h2>
                    <p className="text-[#555555] whitespace-pre-line">{selectedOffer.description}</p>
                  </div>
                  <div className="bg-white p-8 border-2 border-gray-200">
                    <h2 className="text-xl font-bold text-[#1A1A2E] mb-4">Requisiti</h2>
                    <p className="text-[#555555] whitespace-pre-line">{selectedOffer.requirements}</p>
                  </div>
                </div>
                <div>
                  <div className="bg-white p-8 border-2 border-[#27AE60] sticky top-24">
                    <h3 className="text-lg font-bold text-[#1A1A2E] mb-4">Interessato?</h3>
                    <p className="text-[#555555] mb-6">Candidati ora per questa posizione.</p>
                    <Link to="/lavora-con-noi/tecnici" className="btn-primary w-full text-center flex items-center justify-center gap-2">
                      <Send size={18} /> Candidati
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  // List view
  return (
    <div data-testid="job-offers-page">
      <Navbar />
      <main>
        <section className="hero-dark py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="accent-line mb-6"></div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
              Offerte di Lavoro
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl">
              Scopri le nostre opportunità lavorative nel settore EPC e industriale.
            </p>
          </div>
        </section>

        <section className="py-16 bg-[#F5F5F7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-12">Caricamento...</div>
            ) : offers.length === 0 ? (
              <div className="text-center py-12">
                <Briefcase className="w-16 h-16 mx-auto mb-4 text-[#888888]" />
                <p className="text-[#555555]">Nessuna offerta di lavoro al momento.</p>
                <p className="text-[#888888] mt-2">Torna a trovarci presto!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {offers.map((offer) => (
                  <Link
                    key={offer.id}
                    to={`/offerte-lavoro/${offer.id}`}
                    className="bg-white border-2 border-gray-200 hover:border-[#1A1A2E] p-6 transition-all group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-[#27AE60] flex items-center justify-center">
                        <Briefcase className="text-white" size={24} />
                      </div>
                      <span className="text-xs font-bold bg-[#1A1A2E] text-white px-3 py-1">
                        {offer.contract_type}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[#1A1A2E] mb-2 group-hover:text-[#E94560] transition-colors">
                      {offer.title}
                    </h3>
                    {offer.company && <p className="text-sm text-[#555555] mb-3">{offer.company}</p>}
                    <div className="flex flex-wrap gap-3 text-sm text-[#888888]">
                      <span className="flex items-center gap-1"><MapPin size={14} />{offer.location}</span>
                      <span className="flex items-center gap-1"><Clock size={14} />{offer.experience_required}</span>
                    </div>
                    {offer.salary_range && <p className="mt-3 text-sm font-bold text-[#27AE60]">{offer.salary_range}</p>}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
